@if (showChecklist()) {
<section aria-labelledby="setup-title"
  class="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
  <div class="grid lg:grid-cols-[minmax(0,1fr)_22rem]">
    <!-- Setup steps -->
    <div class="p-5 sm:p-7">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h2 id="setup-title" class="text-xl font-semibold text-gray-900 dark:text-white">
            Prepara tu agenda para recibir citas
          </h2>
          <p class="mt-1.5 max-w-prose text-sm text-gray-500 dark:text-gray-400">
            @if (onboardingPendingSteps() === 1) {
            Te falta un paso.
            } @else {
            Te faltan {{ onboardingPendingSteps() }} pasos.
            }
            Al terminar, tus clientes podrán reservar en tu calendario.
          </p>
        </div>
        <button type="button" (click)="refreshSetupStatus()" [disabled]="checklistLoading()"
          class="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-wait dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-200">
          <svg class="size-3.5" [ngClass]="{ 'motion-safe:animate-spin': checklistLoading() }" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
            <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
          </svg>
          {{ checklistLoading() ? 'Actualizando…' : 'Actualizar' }}
        </button>
      </div>

      <ol class="mt-7" aria-label="Pasos de configuración">
        @for (step of setupSteps(); track step.key; let i = $index, last = $last) {
        <li class="relative flex gap-4" [class.pb-7]="!last" [attr.aria-current]="step.current ? 'step' : null">
          <!-- Rail connecting this step to the next one -->
          @if (!last) {
          <span aria-hidden="true" class="absolute left-[15px] top-9 bottom-1 w-0.5 rounded-full"
            [ngClass]="step.done ? 'bg-emerald-400/60 dark:bg-emerald-500/40' : 'bg-gray-200 dark:bg-gray-700'"></span>
          }

          <!-- Marker -->
          @if (step.done) {
          <span aria-hidden="true"
            class="relative flex size-8 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
            <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </span>
          } @else if (step.current) {
          <span aria-hidden="true"
            class="relative flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-500 text-sm font-semibold text-white ring-4 ring-brand-500/15">
            {{ i + 1 }}
          </span>
          } @else {
          <span aria-hidden="true"
            class="relative flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-gray-200 bg-white text-sm font-semibold text-gray-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-500">
            {{ i + 1 }}
          </span>
          }

          <!-- Content -->
          <div class="min-w-0 flex-1 pt-1">
            <p class="text-sm font-semibold"
              [ngClass]="step.done ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'">
              {{ step.title }}
              <span class="sr-only">{{ step.done ? '(completado)' : '(pendiente)' }}</span>
            </p>
            <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {{ step.done ? step.doneDescription : step.description }}
            </p>

            @if (step.current) {
            <a [routerLink]="step.link"
              class="mt-3 inline-flex items-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow-theme-xs hover:bg-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900">
              {{ step.action }}
            </a>
            } @else if (!step.done) {
            <a [routerLink]="step.link"
              class="mt-2 inline-flex text-sm font-medium text-brand-600 underline-offset-4 hover:underline focus:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-brand-400">
              {{ step.action }}
            </a>
            }
          </div>
        </li>
        }
      </ol>
    </div>

    <!-- Week preview: each completed step adds a layer to the miniature agenda -->
    <div aria-hidden="true"
      class="border-t border-gray-100 bg-gray-50 p-5 sm:p-7 lg:border-l lg:border-t-0 dark:border-gray-800 dark:bg-gray-900/50">
      <div class="flex items-center gap-2.5">
        <span class="flex size-7 items-center justify-center rounded-full transition-colors duration-500"
          [ngClass]="checklist().hasProfessionals
            ? 'bg-brand-500 text-white'
            : 'border border-dashed border-gray-300 text-gray-300 dark:border-gray-600 dark:text-gray-600'">
          <svg class="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
            stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21a8 8 0 0 1 16 0" />
          </svg>
        </span>
        <span class="h-2 w-24 rounded-full bg-gray-200 dark:bg-gray-700"></span>
      </div>

      <div class="mt-4 grid grid-cols-7 gap-1.5">
        @for (day of previewDays; track $index; let d = $index) {
        <div class="flex flex-col items-center gap-1.5">
          <span class="text-[11px] font-medium text-gray-400 dark:text-gray-500">{{ day.label }}</span>
          <div class="relative h-20 w-full overflow-hidden rounded-md sm:h-36 transition-colors duration-500"
            [ngClass]="checklist().hasSchedule && day.open
              ? 'bg-brand-50 ring-1 ring-inset ring-brand-100 dark:bg-brand-500/10 dark:ring-brand-500/20'
              : 'bg-white ring-1 ring-inset ring-gray-200 dark:bg-gray-900 dark:ring-gray-800'">
            @if (checklist().hasServices && (day.open || !checklist().hasSchedule)) {
            @for (appt of previewAppointments; track $index) {
            @if (appt.day === d) {
            <span class="absolute inset-x-1 rounded-[3px] bg-brand-500/80 dark:bg-brand-400/70"
              [style.top.%]="appt.top" [style.height.%]="appt.height"></span>
            }
            }
            }
          </div>
        </div>
        }
      </div>

      <p class="mt-4 text-xs text-gray-500 dark:text-gray-400">
        Tu semana se completa con cada paso.
      </p>
    </div>
  </div>
</section>
}

<section class="ag-board" aria-label="Calendario de citas">
  <!-- Toolbar: where am I, how do I move, what can I do -->
  <header class="ag-toolbar">
    <div class="ag-toolbar__nav">
      <div class="ag-arrows">
        <button type="button" class="ag-icon-btn" (click)="goPrev()" aria-label="Periodo anterior">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <button type="button" class="ag-icon-btn" (click)="goNext()" aria-label="Periodo siguiente">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
        </button>
      </div>
      <button type="button" class="ag-btn ag-btn--ghost" (click)="goToday()" [disabled]="rangeHasToday()">
        Hoy
      </button>
      <h2 class="ag-title" aria-live="polite">{{ viewTitle() }}</h2>
    </div>

    <div class="ag-toolbar__actions">
      <div class="ag-segment" role="radiogroup" aria-label="Vista del calendario">
        @for (v of viewOptions(); track v.type) {
        <button type="button" role="radio" [attr.aria-checked]="viewType() === v.type" (click)="setView(v.type)">
          {{ v.label }}
        </button>
        }
      </div>
      <button type="button" class="ag-btn ag-btn--ghost" (click)="onCreateBlock()" [disabled]="!canSchedule()">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect width="18" height="11" x="3" y="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        <span class="ag-hide-sm">Bloquear horario</span>
      </button>
      <button type="button" class="ag-btn ag-btn--primary" (click)="onNewAppointment()" [disabled]="!canSchedule()">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
          stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
        Nueva cita
      </button>
    </div>
  </header>

  <!-- What the colors mean, and the shortcut for creating -->
  <div class="ag-meta">
    <ul class="ag-legend" aria-label="Estados de las citas">
      @for (s of statusLegend(); track s.label) {
      <li><span class="ag-dot" [style.background]="s.color" aria-hidden="true"></span>{{ s.label }}</li>
      }
      <li>
        <svg class="ag-paid" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
        Pagada
      </li>
    </ul>
    @if (canSchedule() && viewType() !== 'listWeek') {
    <p class="ag-hint">Haz clic o arrastra sobre un horario libre para agendar.</p>
    }
  </div>

  <div #calWrap class="ag-calendar w-full min-w-0">
    <full-calendar #calendar *ngIf='calendarVisible()' [options]='calendarOptions()'>
      <!-- Day headers: weekday + date, today highlighted -->
      <ng-template #dayHeaderContent let-arg>
        @if (arg.view.type === 'dayGridMonth') {
        <span class="ag-dayhead__wd">{{ arg.text }}</span>
        } @else if (arg.view.type !== 'listWeek') {
        <span class="ag-dayhead" [class.ag-dayhead--today]="arg.isToday">
          <span class="ag-dayhead__wd">{{ arg.date | date: 'EEE' }}</span>
          <span class="ag-dayhead__num">{{ arg.date | date: 'd' }}</span>
        </span>
        } @else {
        {{ arg.text }}
        }
      </ng-template>

      <ng-template #eventContent let-arg>
        @let xp = arg.event.extendedProps;
        @if (isBlockEvent(arg.event)) {
        <div class="ag-block">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
          <span>{{ blockLabel(arg.event) }}</span>
        </div>
        } @else if (arg.view.type === 'listWeek') {
        <div class="ag-list-row">
          <span class="ag-list-row__name">{{ xp.clientName || arg.event.title }}</span>
          @if (xp.serviceName) {
          <span class="ag-list-row__service">{{ xp.serviceName }}</span>
          }
          @if (xp.professionalName) {
          <span class="ag-list-row__service">con {{ xp.professionalName }}</span>
          }
          <span class="ag-status" [style.--status-color]="xp.statusColor">{{ xp.statusLabel }}</span>
          @if (isEventPaid(arg.event)) {
          <span class="ag-status ag-status--paid">Pagada</span>
          }
        </div>
        } @else if (arg.view.type === 'dayGridMonth') {
        <div class="ag-chip" [attr.title]="arg.timeText + ' ' + (xp.clientName || arg.event.title) + ' (' + xp.statusLabel + ')'">
          <span class="ag-dot" [style.background]="xp.statusColor" aria-hidden="true"></span>
          <span class="ag-chip__time">{{ arg.timeText }}</span>
          <span class="ag-chip__name">{{ xp.clientName || arg.event.title }}</span>
        </div>
        } @else {
        <div class="ag-event" [attr.title]="(xp.clientName || arg.event.title) + (xp.serviceName ? ' (' + xp.serviceName + ')' : '') + ', ' + xp.statusLabel">
          <div class="ag-event__top">
            <span class="ag-event__time">{{ arg.timeText }}</span>
            <span class="ag-event__flags">
              @if (isEventPaid(arg.event)) {
              <svg class="ag-paid" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-label="Pagada"><path d="M20 6 9 17l-5-5" /></svg>
              }
              <span class="ag-dot" [style.background]="xp.statusColor" [attr.aria-label]="xp.statusLabel"></span>
            </span>
          </div>
          <p class="ag-event__name">{{ xp.clientName || arg.event.title }}</p>
          @if (xp.serviceName) {
          <p class="ag-event__service">{{ xp.serviceName }}</p>
          }
        </div>
        }
      </ng-template>
    </full-calendar>
  </div>
</section>


<app-modal [open]="openForm" [title]="titleSelected()" [size]="'2xl'" [bodyComponent]="EventAddForm"
  [closeOnBackdrop]="false" [bodyInputs]="modalBodyInputs" [bodyOutputs]="{ finalPriceChange: onFinalPriceChange }"
  (close)="openForm = false" (confirm)="onModalConfirm($event)" [isSaving]="savingEvent()" confirmButtonText="Agendar">
</app-modal>

<app-modal [open]="openBlockForm" [title]="'Bloquear horario'" [size]="'md'" [bodyComponent]="ScheduleBlockAddForm"
  [closeOnBackdrop]="false" [bodyInputs]="modalBodyInputsBlock" (close)="openBlockForm = false"
  (confirm)="onBlockModalConfirm($event)" confirmButtonText="Bloquear horario" [isSaving]="savingBlock()">
</app-modal>

<app-modal [open]="open" [size]="eventModalSize()" [bodyComponent]="AgendaEventView" [closeOnBackdrop]="true" [fillBody]="true"
  [bodyInputs]="modalBodyInputsEventView" (close)="open = false; eventModalSize.set('sm')" [hasCustomHeader]="true" [hasCustomFooter]="true"
  [bodyOutputs]="{
    saved: onEventSaved,
    openPayment: onEventPayment,
    sizeChanged: onEventSizeChanged,
    paymentCompleted: onPaymentCompleted
  }">
  <div modal-header class="ev-header">
    <p class="ev-header__title">Detalle de la cita</p>
    <div class="ev-header__actions">
      <button type="button" class="ag-icon-btn ag-icon-btn--round" (click)="onQuickEdit(selectedEventId()!)"
        aria-label="Editar cita" title="Editar cita">
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke-width="1.6" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
        </svg>
      </button>
      <button type="button" class="ag-icon-btn ag-icon-btn--round ag-icon-btn--danger" (click)="openDelete(selectedEventId()!)"
        aria-label="Eliminar cita" title="Eliminar cita">
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke-width="1.6" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
        </svg>
      </button>
      <span class="ev-header__sep" aria-hidden="true"></span>
      <button type="button" class="ag-icon-btn ag-icon-btn--round" (click)="open = false; eventModalSize.set('sm')"
        aria-label="Cerrar" title="Cerrar">
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  </div>
</app-modal>


<app-confirm-modal [(open)]="confirmOpen" title="Eliminar cita"
  subtitle="La cita se borrará del calendario. Esta acción no se puede deshacer." cancelText="No, volver" confirmText="Sí, eliminar"
  variant="danger" [closeOnBackdrop]="true" (confirm)="onQuickDelete()" (cancel)="toDeleteId = null">
</app-confirm-modal>

<app-confirm-modal [(open)]="outsideHoursConfirmOpen" title="Horario fuera del negocio"
  subtitle="Estás intentando agendar en un horario que está fuera del horario del negocio. ¿Deseas continuar de todas formas?"
  cancelText="Cancelar" confirmText="Agendar de todas formas" variant="warning" [closeOnBackdrop]="true"
  (confirm)="onConfirmOutsideHours()" (cancel)="onCancelOutsideHours()">
</app-confirm-modal>

<app-confirm-modal [(open)]="deleteBlockConfirmOpen" title="Desbloquear horario"
  subtitle="El horario vuelve a quedar disponible para agendar citas."
  cancelText="Cancelar" confirmText="Sí, desbloquear" variant="danger" [closeOnBackdrop]="true"
  (confirm)="onConfirmDeleteBlock()" (cancel)="onCancelDeleteBlock()">
</app-confirm-modal>
